import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { FormGroup, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { DAYS_OF_WEEK, SHIFT } from "config/AppConfig";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import moment from "moment-timezone";
import SelectField from 'components/Select/SelectField';
import SelectTime from 'components/Select/SelectTime';
import { Label } from 'reactstrap';
import SegmentFieldArray from 'views/Company/Jobs/component/SegmentFieldArray';

const useStyles = makeStyles(() => ({
  textarea: {
    height: '100px !important',
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '1rem'
  },
  radio: {
    color: AppColor.black,
    "& .MuiFormControlLabel-label": {
      fontSize: '1rem',
      fontWeight: 700
    },
  },
  radioGroup: {
    flexDirection: 'row'
  },
  postDescription: {
    border: '1px solid rgba(0, 0, 0, 0.2)',
    padding: '0.5rem 1rem',
    color: AppColor.black
  },
  bold: {
    fontWeight: 700
  },
  wrapShift: {
    border: '1px solid #c4b5bc',
    padding: '1rem',
    boxShadow: '0px 4px 4px rgb(0 0 0 / 25%)',
    borderRadius: 4,
    marginBottom: '0.5rem',
    '& .shiftInfo': {
      margin: '-1rem -1rem 1rem -1rem',
      padding: '0.3rem 1rem',
      border: '1px solid #c4b5bc',
      borderRadius: 4,
      backgroundColor: AppColor.white
    }
  },
  collapseShift: {
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 1rem',
    margin: '0.5rem 0',
    '& .shiftInfo': {
      margin: 0,
      padding: '0.3rem 1rem',
      border: '1px solid #c4b5bc',
      borderRadius: 4,
      flex: '1 0 100%',
      backgroundColor: AppColor.white
    },
    '& .remove-icon': {
      fontSize: '2rem',
      color: AppColor.red
    }
  },
  title: {
    display: 'flex',
    margin: '2rem 0 1rem 0',
    '& h5': {
      color: AppColor.black,
    }
  },
  calendar: {
    cursor: 'pointer'
  }
}));

const KEY = {
  conflict: 'conflict-',
  startEnd: 'start-end-'
}
const ShiftForm = props => {
  const {
    onSubmit,
    onCancel,
    newBooking,
    checkValidateBookingTime,
    setInvalidMap,
    invalidMap,
    getValidateMinMaxTime
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [backupBooking, setBackupBooking] = useState();
  const { dayOfWeek: selectedDate, 
    values: list, 
    shiftType, startDate, start, end, segments } = newBooking;

    useEffect(() => {
      setBackupBooking(newBooking);
    }, []);

  const dayOptions = DAYS_OF_WEEK.map(item => {
    return {
      value: item,
      label: t(`daysOfTheWeek.${item}`)
    }
  });
  const shiftOptions = SHIFT.map(item => {
    return {
      value: item,
      label: t(`shift.${item}`)
    }
  });

  const defaultWorkHistory = {
    dayOfWeek: selectedDate
      ? selectedDate
      : dayOptions.length > 0
        ? dayOptions[0].value
        : '',
    shiftType: shiftType
      ? shiftType
      : shiftOptions.length > 0
        ? shiftOptions[0].value
        : '',
    startTime: start ? start : '',
    endTime: end ? end : '',
    numberWorkers: '',
    isExpand: true,
    segments: segments ? segments : []
  };


  const _handleSelectChangeEndTime = (startTime, event, setFieldValue) => {
    const { type, values, item } = newBooking;
    const newValues = { ...values };
    const index = values?.[item.key]?.shift?.findIndex(e => e.type === type);
    const weekDate = moment(new Date(values?.[item.key].date));
    if (index > -1) {
      event = moment(new Date(event)).set({
        year: weekDate.get('year'),
        month: weekDate.get('month'),
        date: weekDate.get('date')
      });
      const momentStartTime = moment(startTime);

      if (moment.range(momentStartTime, event).diff('minutes') <= 0) {
        event.add(1, 'days');
      }
      newValues[item.key].shift[index]['end'] = event.format('YYYY/MM/DD HH:mm');

      // check getValidateMinMaxTime to setInvalidMap
      const textError = getValidateMinMaxTime(
        newValues[item.key].shift[index].start,
        newValues[item.key].shift[index].end
      )
      const newInvalidMap = new Map(invalidMap);
      const key = KEY.startEnd + momentStartTime.format('YYYY/MM/DD') + newValues[item.key].shift[index].shiftType;
      if (textError === null && newInvalidMap.has(key)) {
        newInvalidMap.delete(key);
        setInvalidMap(newInvalidMap);
      }
      if (textError !== null && !newInvalidMap.has(key)) {
        newInvalidMap.set(key, textError);
        setInvalidMap(newInvalidMap);
      }
      checkValidateBookingTime(newValues[item.key]?.shift, newValues[item.key]?.date)
    }

    const momentStartTime = moment(startTime);
    if (moment.range(momentStartTime, event).diff('minutes') <= 0) {
      event.add(1, 'days');
    }
      setFieldValue('endTime', event.format('YYYY/MM/DD HH:mm'))
  }
  const _handleSelectChangeStartTime = (endTime, event, setFieldValue) => {
    const { type, values, item } = newBooking;
    const newValues = { ...values };
    const index = values?.[item.key]?.shift?.findIndex(e => e.type === type);
    const weekDate = moment(new Date(values?.[item.key].date));
    if (index > -1) {
      event = moment(new Date(event)).set({
        year: weekDate.get('year'),
        month: weekDate.get('month'),
        date: weekDate.get('date')
      });
      const momentEndTime = moment(endTime);

      if (moment.range(event, momentEndTime).diff('minutes') <= 0) {
        momentEndTime.add(1, 'days');
        newValues[item.key].shift[index]['end'] = momentEndTime.format('YYYY/MM/DD HH:mm');
      }
      newValues[item.key].shift[index]['start'] = event.format('YYYY/MM/DD HH:mm');

      // check getValidateMinMaxTime to setInvalidMap
      const textError = getValidateMinMaxTime(
        newValues[item.key].shift[index].start,
        newValues[item.key].shift[index].end
      )
      const newInvalidMap = new Map(invalidMap);
      const key = KEY.startEnd + event.format('YYYY/MM/DD') + newValues[item.key].shift[index].shiftType;
      if (textError === null && newInvalidMap.has(key)) {
        newInvalidMap.delete(key);
        setInvalidMap(newInvalidMap);
      }
      if (textError !== null && !newInvalidMap.has(key)) {
        newInvalidMap.set(key, textError);
        setInvalidMap(newInvalidMap);
      }
      checkValidateBookingTime(newValues[item.key]?.shift, newValues[item.key]?.date)
    }
    const momentEndTime = moment(endTime);
    if (moment.range(event, momentEndTime).diff('minutes') <= 0) {
      momentEndTime.add(1, 'days');
      setFieldValue('endTime', momentEndTime.format('YYYY/MM/DD HH:mm'));
    }
      setFieldValue('startTime', event.format('YYYY/MM/DD HH:mm'));
  }

  const segmentValidate = Yup.object()
    .shape(
      {
        endTime: Yup.date()
          .required("validation.required")
          .when("startTime", (startTime, schema) => {
            if (startTime && startTime !== "") {
              return schema.min(
                moment(startTime)
                  .add(1, "minutes")
                  .format("yyyy/MM/DD HH:mm"),
                "validation.endTimeMustBeAfterStartTime"
              );
            }
          }),
        startTime: Yup.date()
          .required("validation.required")
          .when("endTime", (endTime, schema) => {
            if (endTime && endTime !== '') {
              return schema.max(
                moment(endTime)
                  .subtract(1, "minutes")
                  .format("yyyy/MM/DD HH:mm"),
                "validation.startTimeMustBeBeforeEndTime"
              );
            }
          }),
        shiftType: Yup.string().required("validation.required"),
      },
      // determine relationship
      [
        ["startTime", "endTime"],
        ["endTime", "startTime"],
      ]
    )
    // .test("duplicates-shift-type", "validation.duplicatesShiftType", function (segment) {
    //   const { path, createError } = this;
    //   const segments = this.parent;
    //   if (!segments) return true;
    //   const shiftTypes = segments.map(seg => seg.shiftType);

    //   // Ki?m tra xem shiftType hi?n t?i co ph?i la duy nh?t khong  
    //   if (shiftTypes.indexOf(segment.shiftType) !== shiftTypes.lastIndexOf(segment.shiftType)) {
    //     return createError({ path: `${path}.shiftType`, message: "shiftType must be unique" });
    //   }
    //   return true;
    // });
  const shiftValidate = Yup.object().shape({
    startTime: Yup.date()
      .required("validation.required")
      .when('endTime', (endTime, schema) => {
        if (endTime && endTime !== '') {
          return schema.max(
            moment(endTime).subtract(1, 'minutes').format('yyyy/MM/DD HH:mm'),
            "validation.startTimeMustBeBeforeEndTime"
          )
        }
      }),
    endTime: Yup.date()
      .required("validation.required")
      .when('startTime', (startTime, schema) => {
        if (startTime && startTime !== '') {
          return schema.min(
            moment(startTime).add(1, 'minutes').format('yyyy/MM/DD HH:mm'),
            "validation.endTimeMustBeAfterStartTime"
          )
        }
      }),
    segments: Yup.array().of(segmentValidate),

    // .test('len', "validation.max_length_10", val => Math.ceil(Math.log10(val + 1)) <= 10)
  },
    [
      ["startTime", "endTime"],
      ["endTime", "startTime"],
    ]
  );

  const handleSubmit = (data) => {
    // setIsLoading(true);
    const { dayOfWeek, ...params } = data;
    params.groupByDayOfWeek = {
      [dayOfWeek]: { status: 'ACTIVE' }
    }
    delete newBooking.segments;
    onSubmit({ ...params, ...newBooking });
  }

  const returnIsConflictTime = (bookingTime, currentBookingTime) => {

    if (moment(bookingTime.startTime).isSame(moment(currentBookingTime.startTime)) ||
        moment(bookingTime.endTime).isSame(moment(currentBookingTime.endTime)) ||
        (moment(bookingTime.startTime).isSame(moment(currentBookingTime.startTime)) && moment(bookingTime.endTime).isSame(moment(currentBookingTime.endTime))) ||
        moment(bookingTime.startTime).isBetween(currentBookingTime.startTime, currentBookingTime.endTime) ||
        moment(bookingTime.endTime).isBetween(currentBookingTime.startTime, currentBookingTime.endTime) ||
        moment(currentBookingTime.startTime).isBetween(bookingTime.startTime, bookingTime.endTime) ||
        moment(currentBookingTime.endTime).isBetween(bookingTime.startTime, bookingTime.endTime)
        ) {
      return true;
    } else {
      if ( ((moment(currentBookingTime.startTime).day() === moment(bookingTime.endTime).day() || moment(currentBookingTime.endTime).day() === moment(bookingTime.startTime).day()) 
        && ((moment(currentBookingTime.startTime).isBefore(moment(bookingTime.endTime)) && !moment(currentBookingTime.endTime).isSameOrBefore(bookingTime.endTime)) || 
        (moment(bookingTime.endTime).isAfter(moment(currentBookingTime.startTime)) && !moment(bookingTime.startTime).isSameOrAfter(moment(currentBookingTime.endTime))) ))
      ) {
        return true;
      } else {
        return false;
      }
    }
  }
  return (
    <React.Fragment>
      <Formik
        initialValues={defaultWorkHistory}
        validationSchema={shiftValidate}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, setFieldValue }) => {

          const {
            shiftType,
            startTime,
            endTime,
          } = values;
          let isConflict = false;

          for (const value of Object.entries(list)) {
            if (typeof value[1] === 'object' && value[1].date && value[1].shift) {
              value[1].shift.map(sh => {
                // console.log('sh', sh);
                if (sh && sh.bookingList && sh.bookingList.length > 0) {
                  sh.bookingList.map(bk => {
                    if (sh.id !== newBooking.id && startTime && endTime) {
                      const momentEndTime = moment(endTime);
                      const momentStartTime = moment(startTime);

                      if (moment.range(moment(sh.end), moment(startTime)).diff('minutes') < 0) {
                        momentStartTime.add('1', 'day');
                        setFieldValue('startTime', momentStartTime.format('YYYY/MM/DD HH:mm'));
                      }
                      if (moment.range(moment(momentStartTime), moment(endTime)).diff('minutes') <= 0) {
                        momentEndTime.add('1', 'day');
                      }
                      if (returnIsConflictTime({startTime: moment(bk.startTime).format('YYYY/MM/DD HH:mm'), endTime: moment(bk.endTime).format('YYYY/MM/DD HH:mm')}, {startTime: moment(momentStartTime).format('YYYY/MM/DD HH:mm'), endTime: moment(momentEndTime).format('YYYY/MM/DD HH:mm') })) {
                        isConflict = true;
                      }
                    }
                  })
                }
                if (sh && sh.start && sh.end && startTime && endTime && sh.id !== newBooking.id) {
                  const momentEndTime = moment(endTime);
                      const momentStartTime = moment(startTime);

                      if (moment.range(moment(sh.end), moment(startTime)).diff('minutes') < 0) {
                        momentStartTime.add('1', 'day');
                        setFieldValue('startTime', momentStartTime.format('YYYY/MM/DD HH:mm'));
                      }
                      if (moment.range(moment(momentStartTime), moment(endTime)).diff('minutes') <= 0) {
                        momentEndTime.add('1', 'day');
                      }
                      if (returnIsConflictTime({startTime: sh.start, endTime: sh.end}, {startTime: moment(momentStartTime).format('YYYY/MM/DD HH:mm'), endTime: moment(momentEndTime).format('YYYY/MM/DD HH:mm') })) {
                        isConflict = true;
                      }
                }
              })
            }
          }
          return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Field
                    component={SelectField}
                    name={`dayOfWeek`}
                    label={"label.daysOfTheWeek"}
                    options={dayOptions}
                    disabled={!!selectedDate}
                    onSelected={() => {
                      setFieldValue(
                        `shiftType`,
                        ""
                      );

                      setFieldValue(
                        `startTime`,
                        moment(selectedDate)
                          .hours(0)
                          .minutes(0)
                          .format("YYYY/MM/DD HH:mm")
                      );
                      setFieldValue(
                        `endTime`,
                        moment(selectedDate)
                          .hours(0)
                          .minutes(0)
                          .format("YYYY/MM/DD HH:mm")
                      );
                    }}
                    required
                  />
                  <Field
                    component={SelectField}
                    name={`shiftType`}
                    label={"label.shift"}
                    options={shiftOptions}
                    onSelected={() => {
                      setFieldValue(
                        `startTime`,
                        ""
                      );
                      setFieldValue(
                        `endTime`,
                        ""
                      );
                    }}
                    required
                    disabled={!!shiftType}
                  />
                  {isConflict && (
                    <Label
                      className={`${classes.label}`}
                      style={{ color: AppColor.red }}
                    >
                      {t("validation.conflictTime")}
                    </Label>
                  )}
                  {/* Start Time End Time */}
                  <Field
                    component={SelectTime}
                    name={`startTime`}
                    onSelected={(event) => _handleSelectChangeStartTime(endTime, event, setFieldValue, isConflict)}
                    label={"label.startTime"}
                    disabled={!shiftType || shiftType === ""}
                    required
                  />
                  <Field
                    component={SelectTime}
                    name={`endTime`}
                    label={"label.endTime"}
                    onSelected={(event) => _handleSelectChangeEndTime(startTime, event, setFieldValue, isConflict)}
                    disabled={!shiftType || shiftType === ""}
                    required
                  />
                  {
                    startTime && endTime && shiftType &&
                    <SegmentFieldArray
                      startDate={moment(startDate)}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      jobAvailability={values}
                      fieldArrayName={`segments`}
                      minTime={moment(startTime)}
                      maxTime={moment(endTime)}
                      isUsingCurrentDate={false}
                    />
                  }
                </Grid>
              </Grid>
              <FormGroup className={classes.control}>
                <AppButton
                  label={`${t("button.close")}`}
                  rounder
                  mode="outlined"
                  onClick={() => onCancel(backupBooking)}
                // isLoading={isSubmitting}
                // disabled={isSubmitting}
                />
                <AppButton
                  className='ml-1'
                  label={`${t("button.save")}`}
                  rounder
                  type="submit"
                  disabled={isConflict}
                  // isLoading={isSubmitting}
                  // disabled={invalidMap.has(KEY.conflict + moment(item.date).format('YYYY/MM/DD') + shiftType)}
                />
              </FormGroup>
            </Form>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}

ShiftForm.propTypes = {
  setIsLoading: PropTypes.func,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  newBooking: PropTypes.any,
  isSubmitting: PropTypes.bool,
  jobAvailabilities: PropTypes.any,
  checkValidDate: PropTypes.func,
  jobDetail: PropTypes.any,
  checkValidateBookingTime: PropTypes.func,
  setInvalidMap: PropTypes.func,
  invalidMap: PropTypes.object,
  getValidateMinMaxTime: PropTypes.func
}

export default ShiftForm;
